import { useRef } from 'react';

import type { circlesSection } from '../../../../app/_shared/marketing/landingContent';
import {
  useWalkthroughFixedFramePin,
  useWalkthroughPinScrollLock,
  useWalkthroughPinTrackHeight,
  useWalkthroughSectionStage,
} from '../../hooks/useWalkthroughSectionStage';
import { useScrollParallax } from '../../hooks/useScrollParallax';
import { useCoarsePointer } from '../../presence/useCoarsePointer';
import { useReducedMotion } from '../../presence/useReducedMotion';
import { WalkthroughPreviewCard } from './CircleWalkthroughPreviews';
import './landingRef.css';

type FeatureGlyph = (typeof circlesSection.featureChips)[number]['glyph'];

function FeatureChipIcon({ glyph }: { glyph: FeatureGlyph }) {
  if (glyph === 'prompt') {
    return (
      <svg className="circle-walkthrough-chip__icon" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path d="M5 8h14v10H5V8Z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
        <path d="M8 12h8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    );
  }
  if (glyph === 'activities') {
    return (
      <svg className="circle-walkthrough-chip__icon" viewBox="0 0 24 24" fill="none" aria-hidden>
        <circle cx="8" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5" />
        <circle cx="16" cy="9" r="2.5" stroke="currentColor" strokeWidth="1.5" />
        <path
          d="M4 18c1.5-2.5 3.5-3.5 5-3.5s3.5 1 5 3.5"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinecap="round"
        />
      </svg>
    );
  }
  if (glyph === 'sideTrail') {
    return (
      <svg className="circle-walkthrough-chip__icon" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M6 6h8l4 4v8H6V6Z"
          stroke="currentColor"
          strokeWidth="1.5"
          strokeLinejoin="round"
        />
        <path d="M14 6v4h4" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      </svg>
    );
  }
  return (
    <svg className="circle-walkthrough-chip__icon" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M6 8.5c0-1.4 1.1-2.5 2.5-2.5h7c1.4 0 2.5 1.1 2.5 2.5v4.5c0 1.4-1.1 2.5-2.5 2.5H11l-3 2.5v-2.5H8.5A2.5 2.5 0 0 1 6 13V8.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
    </svg>
  );
}

type Props = {
  h2: string;
  subtitle: string;
  timeline: typeof circlesSection.timeline;
  previews: typeof circlesSection.previews;
  featureChips: typeof circlesSection.featureChips;
};

export function CirclesExplanationSection({
  h2,
  subtitle,
  timeline,
  previews,
  featureChips,
}: Props) {
  const reducedMotion = useReducedMotion();
  const coarsePointer = useCoarsePointer();
  const trackRef = useRef<HTMLDivElement>(null);
  const frameRef = useRef<HTMLDivElement>(null);
  const spacerRef = useRef<HTMLDivElement>(null);
  const timelineColRef = useRef<HTMLDivElement>(null);
  const previewColRef = useRef<HTMLDivElement>(null);

  const { pinned, activeDay, chipsVisible, progressRatio } = useWalkthroughSectionStage(trackRef);
  useWalkthroughPinTrackHeight(trackRef, frameRef, { disabled: reducedMotion });
  // Touch momentum scrolling fights a scrollTo-based speed clamp (reads as bounce/jitter on
  // phones), so let touch scroll drive the pin directly instead of capping its speed.
  useWalkthroughPinScrollLock(trackRef, { disabled: reducedMotion || coarsePointer });
  useWalkthroughFixedFramePin(trackRef, frameRef, spacerRef, { disabled: reducedMotion });

  // Strength × 96px travel is the peak offset (see useScrollParallax): 0.06 gave under 6px, which
  // is below what anyone notices on a 900px viewport. 0.11 lands the columns at ~10px in opposite
  // directions — enough to read as depth while pinned, small enough that the frame still reads as
  // one locked card.
  useScrollParallax(timelineColRef, 0.11, {
    disabled: reducedMotion,
    maxPx: 14,
    enabled: pinned,
  });
  useScrollParallax(previewColRef, 0.11, {
    disabled: reducedMotion,
    maxPx: 16,
    invert: true,
    enabled: pinned,
  });

  return (
    <section className="circle-walkthrough" aria-labelledby="circle-inside-heading">
      <div ref={trackRef} className="circle-walkthrough__pinTrack">
        <div ref={spacerRef} className="circle-walkthrough__pinSpacer" aria-hidden />
        <div
          ref={frameRef}
          className={`circle-walkthrough__pinFrame${pinned || chipsVisible ? ' is-pinned' : ''}`}>
          <header className="circle-walkthrough__header">
            <h2 id="circle-inside-heading" className="circle-walkthrough__h2 ml-heading-serif">
              {h2}
            </h2>
            <p className="circle-walkthrough__subtitle ml-text-body-secondary">{subtitle}</p>
          </header>

          <div className="circle-walkthrough__stage" aria-label="Three-day Circle flow">
            <div
              ref={timelineColRef}
              className="ml-parallaxScope circle-walkthrough__timelineCol"
              data-reduced-motion={reducedMotion ? 'true' : undefined}>
              <div className="ml-parallaxLift">
                <ol className="circle-walkthrough__timeline">
                  {timeline.map((step) => {
                    const isActive = activeDay === step.day;
                    const isDone = activeDay > step.day;
                    return (
                      <li
                        key={step.day}
                        className={`circle-walkthrough__timelineCard${isActive ? ' circle-walkthrough__timelineCard--active' : ''}${isDone ? ' circle-walkthrough__timelineCard--done' : ''}`}
                        aria-current={isActive ? 'step' : undefined}>
                        <span className="circle-walkthrough__timelineDot" aria-hidden>
                          {step.day}
                        </span>
                        <div className="circle-walkthrough__timelineCopy">
                          <p className="circle-walkthrough__dayLabel">{step.dayLabel}</p>
                          <h3 className="circle-walkthrough__timelineTitle ml-heading-sans">
                            {step.title}
                          </h3>
                          <p className="circle-walkthrough__timelineBody">{step.body}</p>
                        </div>
                      </li>
                    );
                  })}
                </ol>
              </div>
            </div>

            <div
              ref={previewColRef}
              className="ml-parallaxScope circle-walkthrough__previewCol"
              data-reduced-motion={reducedMotion ? 'true' : undefined}>
              <div className="ml-parallaxLift circle-walkthrough__previewStack">
                {previews.map((preview) => {
                  const isActive = activeDay === preview.day;
                  const showPreview = isActive || reducedMotion;
                  return (
                    <div
                      key={preview.day}
                      className={`circle-walkthrough__previewLayer${showPreview ? ' is-active' : ''}`}
                      aria-hidden={!showPreview}>
                      <WalkthroughPreviewCard
                        preview={preview}
                        isActive={isActive}
                        revealed={showPreview}
                      />
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          <div
            className={`circle-walkthrough__chips${chipsVisible || reducedMotion ? ' is-revealed' : ''}`}
            aria-label="Circle tools"
            aria-hidden={!(chipsVisible || reducedMotion)}>
            {featureChips.map((chip, index) => {
              // Spread over the full pin progress (not activeDay) so the chip count can exceed
              // the day count — otherwise the last chip(s) never reach an "active" state.
              const activeChipIndex = reducedMotion
                ? featureChips.length - 1
                : Math.min(
                    featureChips.length - 1,
                    Math.floor(progressRatio * featureChips.length)
                  );
              const isActiveChip = (chipsVisible || reducedMotion) && index === activeChipIndex;
              return (
                <div
                  key={chip.label}
                  className={`circle-walkthrough-chip${isActiveChip ? ' circle-walkthrough-chip--active' : ''}`}
                  style={
                    chipsVisible && !reducedMotion
                      ? ({ animationDelay: `${index * 90}ms` } as const)
                      : undefined
                  }>
                  <FeatureChipIcon glyph={chip.glyph} />
                  <div className="circle-walkthrough-chip__text">
                    <span className="circle-walkthrough-chip__label">{chip.label}</span>
                    <span className="circle-walkthrough-chip__micro">{chip.micro}</span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
